// How a browser gets into a quote, for every kind of quote. A visit
// arrives with a one-time link (`?t=`) or with nothing but whatever device
// session cookie it already holds; either ends in the quote's view, a
// refusal the visitor reads on the interstitial, or a dead end. A visitor
// who is signed in may also exchange their own session for the quote's.
// What a quote kind adds on top (a signed handoff, a confirmation gate)
// plugs into these outcomes rather than repeating the flow.

import { NetworkError } from "hooks/usePublicAPI";

import type { QuoteType } from "./analytics";
import type { QuotePresence, SessionRefusal } from "./api";

export const NO_CREDENTIAL_REASON = "no_credential";

/** A mint refusal that only says this link can't be used again. The
 * browser may still hold a session from the first time it was used. */
const SPENT_LINK_REASONS: ReadonlySet<string> = new Set([
  "quote_link_redeemed",
  "quote_link_expired",
  "quote_link_revoked",
]);

const SIGNATURE_REASONS: readonly string[] = [
  "quote_signature_expired",
  "quote_signature_redeemed",
  "quote_signature_unbound",
];

/** The refusals worth a signed-in exchange: the visitor may still be one
 * of the quote's contacts. Any other reason is a credential meant for
 * someone else and stays refused. Only a policy is ever opened with a
 * signed handoff. */
const SIGNED_IN_RETRY_REASONS: Record<QuoteType, ReadonlySet<string>> = {
  policy: new Set([
    NO_CREDENTIAL_REASON,
    ...SPENT_LINK_REASONS,
    ...SIGNATURE_REASONS,
  ]),
  case: new Set([NO_CREDENTIAL_REASON, ...SPENT_LINK_REASONS]),
};

/** A visitor who just proved an email or phone on the re-entry screen
 * (`afterReentry`) gets an exchange whatever the refusal said: the proof,
 * not the credential, is what may open the quote now. */
export function signedInExchangeCanHelp(
  subject: QuoteType,
  reason: string,
  afterReentry = false,
): boolean {
  return afterReentry || SIGNED_IN_RETRY_REASONS[subject].has(reason);
}

export function shouldTrySignedInSession(args: {
  subject: QuoteType;
  reason: string;
  signedIn: boolean;
  /** One exchange per arrival: a blank 404 is final. */
  untried: boolean;
  afterReentry?: boolean;
}): boolean {
  return (
    args.signedIn &&
    args.untried &&
    signedInExchangeCanHelp(args.subject, args.reason, args.afterReentry)
  );
}

/** Why a quote can no longer be opened by this browser, and where the quote
 * stands. `body` is the 409 body as sent, for a quote kind whose refusals
 * carry more (undefined for a visit that never presented a credential). */
export interface Refusal {
  reason: string;
  presence: QuotePresence | null;
  body: unknown;
}

/** Reads a 409 mint body. */
export function refusalFromBody(data: unknown): Refusal {
  const body: Partial<SessionRefusal> =
    typeof data === "object" && data !== null ? data : {};
  return {
    reason: typeof body.error === "string" ? body.error : "unknown",
    presence: body.presence ?? null,
    body: data,
  };
}

export type AccessOutcome<V> =
  | { kind: "granted"; view: V }
  | ({ kind: "refused" } & Refusal)
  | { kind: "not_found"; operation: string; status: number | null }
  | { kind: "error"; operation: string; status: number | null };

export interface AccessDeps<V> {
  /** Presents the URL's link; absent when the visit carries none. */
  mintLink?: () => Promise<unknown>;
  fetchView: () => Promise<V>;
  /** Where the quote stands, for a visit with no credential at all. A
   * kind of quote that doesn't use it leaves it out. */
  fetchPresence?: () => Promise<QuotePresence>;
  /** Takes the link out of the address bar. */
  stripLink: () => void;
  /** Reports a failure the visitor can do nothing about. */
  report: (operation: string, error: unknown) => void;
}

function statusOf(error: unknown): number | null {
  return error instanceof NetworkError ? error.statusCode : null;
}

function isStatus(error: unknown, ...statuses: number[]): boolean {
  const status = statusOf(error);
  return status !== null && statuses.includes(status);
}

function failed<V>(
  deps: Pick<AccessDeps<V>, "report">,
  operation: string,
  error: unknown,
): AccessOutcome<V> {
  deps.report(operation, error);
  return { kind: "error", operation, status: statusOf(error) };
}

async function noCredential<V>(deps: AccessDeps<V>): Promise<AccessOutcome<V>> {
  if (!deps.fetchPresence) {
    return {
      kind: "refused",
      reason: NO_CREDENTIAL_REASON,
      presence: null,
      body: undefined,
    };
  }
  try {
    const presence = await deps.fetchPresence();
    return {
      kind: "refused",
      reason: NO_CREDENTIAL_REASON,
      presence,
      body: undefined,
    };
  } catch (error) {
    deps.report("fetch_presence", error);
    return {
      kind: "not_found",
      operation: "fetch_presence",
      status: statusOf(error),
    };
  }
}

/** Reads the view with whatever session cookie the browser holds. A view
 * that answers 401 or 404 means the browser has no session for this quote. */
async function readWithCookie<V>(
  deps: AccessDeps<V>,
  onNoSession: () => Promise<AccessOutcome<V>>,
): Promise<AccessOutcome<V>> {
  try {
    return { kind: "granted", view: await deps.fetchView() };
  } catch (error) {
    if (isStatus(error, 401, 404)) return onNoSession();
    return failed(deps, "fetch_view", error);
  }
}

/** Opens a quote from a visit that presents no link. */
export function openWithCookie<V>(
  deps: AccessDeps<V>,
): Promise<AccessOutcome<V>> {
  return readWithCookie(deps, () => noCredential(deps));
}

/** Opens a quote from a visit that presents a one-time link. The link
 * leaves the address bar once the server has answered it either way; a
 * fault keeps it so a reload can try again. A link that is spent, expired
 * or revoked falls back to the browser's own session: it shows the quote
 * when that session is valid and the refusal when it is not. */
export async function openWithLink<V>(
  deps: AccessDeps<V> & { mintLink: () => Promise<unknown> },
): Promise<AccessOutcome<V>> {
  try {
    await deps.mintLink();
  } catch (error) {
    if (isStatus(error, 409)) {
      deps.stripLink();
      const refusal = refusalFromBody((error as NetworkError).responseData);
      const refused: AccessOutcome<V> = { kind: "refused", ...refusal };
      if (!SPENT_LINK_REASONS.has(refusal.reason)) return refused;
      return readWithCookie(deps, async () => refused);
    }
    if (isStatus(error, 404)) {
      deps.stripLink();
      return { kind: "not_found", operation: "mint_session", status: 404 };
    }
    return failed(deps, "mint_session", error);
  }
  deps.stripLink();
  return openWithCookie(deps);
}

/** Exchanges the signed-in visitor's own session for the quote's, then
 * reads the view. Null when it did not open the quote: a 404 means the
 * visitor is not a contact on it, and any other failure is reported. */
export async function exchangeSignedInSession<V>(deps: {
  exchange: () => Promise<unknown>;
  fetchView: () => Promise<V>;
  report: (operation: string, error: unknown) => void;
}): Promise<V | null> {
  try {
    await deps.exchange();
    return await deps.fetchView();
  } catch (error) {
    if (!isStatus(error, 404)) deps.report("signed_in_session", error);
    return null;
  }
}
